import { useEffect, useState } from 'react';

import { CrashScreen } from '~shared/observability/crash-screen';
import {
  rememberReportedError,
  reportError,
} from '~shared/observability/report-error';

interface ErrorFallbackProps {
  error?: unknown;
  componentStack?: string;
  eventId?: string;
  resetError?: () => void;
}

/**
 * Top-level safety net rendered by Sentry.ErrorBoundary when an uncaught render
 * error escapes the router. When Sentry is initialised the boundary supplies a
 * real `eventId`; when it is skipped (local / development / staging) we still
 * go through `reportError` so the path matches route crashes — and get no
 * fabricated reference code if capture is a no-op.
 */
export const ErrorFallback = ({
  error,
  componentStack,
  eventId: boundaryEventId,
  resetError,
}: ErrorFallbackProps) => {
  const [eventId, setEventId] = useState<string | undefined>(boundaryEventId);

  useEffect(() => {
    if (boundaryEventId) {
      rememberReportedError(error, boundaryEventId);
      setEventId(boundaryEventId);
      return;
    }
    setEventId(reportError(error, { featureArea: 'boundary' }));
  }, [error, boundaryEventId]);

  return (
    <CrashScreen
      error={error}
      componentStack={componentStack}
      eventId={eventId}
      onRetry={resetError}
    />
  );
};
